import type { ReactNode } from "react";
import type { StatItem } from "../Types/StatItemType";

/** A stat value is "empty" when it is null/undefined or a blank/whitespace-only
 *  string. Such stats render a muted em-dash placeholder instead of leaving a
 *  blank gap under the label. Mirrors the em-dash convention used by TextCell.
 *
 *  `value` is a shared `ReactNode`, which includes `null`; catching it keeps the
 *  em-dash behaviour the old `?? "—"` call-sites had, for any consumer whose
 *  value can still be null (not just the hubble fields that map null→undefined). */
export function isEmptyStatValue(value: ReactNode): boolean {
  return (
    value === undefined ||
    // oxlint-disable-next-line fs/no-null -- shared ReactNode can be null; treat it as empty
    value === null ||
    (typeof value === "string" && value.trim() === "")
  );
}

/** Renders a percentage value with threshold coloring, or a muted em-dash for
 *  empty values so a stat never shows just a label over an empty gap. */
export function formatStatValue(item: StatItem): ReactNode {
  if (item.percentage) {
    const { value, threshold, secondary } = item.percentage;
    const color = value >= threshold ? "text-dark-green" : "text-orange";
    const primary = (
      <span className={`font-bold ${color}`}>{value.toFixed(1)}%</span>
    );
    if (secondary) {
      const secColor =
        secondary.value >= secondary.threshold
          ? "text-dark-green"
          : "text-orange";
      return (
        <span className="font-bold">
          {primary} <span className="text-content-muted">(</span>
          <span className={secColor}>{secondary.value.toFixed(1)}%</span>
          <span className="text-content-muted">)</span>
        </span>
      );
    }
    return primary;
  }
  if (isEmptyStatValue(item.value)) {
    return <span className="text-content-faint">{"—"}</span>;
  }
  return item.value;
}
